import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { useEditorChrome } from '@/components/app-editor/use-editor-chrome';
import {
  VisualEditorLayout,
  type PublishState,
} from '@/components/app-editor/visual-editor-layout';
import {
  CouponColorPanel,
  CouponEffectPanel,
  CouponPositionPanel,
  CouponTextPanel,
} from '@/components/coupon-banner/coupon-banner-panels';
import { CouponStudioPreview } from '@/components/coupon-banner/coupon-studio-preview';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { useBlockerFull } from '@/hooks/use-blocker-full';
import {
  useGetCouponBanner,
  useSetCouponBanner,
} from '@/hooks/use-coupon-banner';
import { useTenant } from '@/hooks/use-tenant';
import { useI18nContext } from '@/i18n/i18n-react';
import { COUPON_MIN_APP_VERSION } from '@/lib/app-editor/app-hub';
import {
  couponDtoToForm,
  couponFormToDto,
  isCouponFormDirty,
  validateCouponForm,
  type CouponBannerForm,
} from '@/lib/app-editor/coupon-banner-form';
import type { CouponBannerDto } from '@/lib/dtos';
import { isVersionGreaterOrEqual } from '@/lib/utils';

type TabId = 'text' | 'color' | 'position' | 'effect';

export default function BannerConfigPage() {
  const navigate = useNavigate();
  const { LL } = useI18nContext();
  const tenant = useTenant();
  const { data, isPending, isError, refetch } = useGetCouponBanner();

  useEffect(() => {
    if (!isVersionGreaterOrEqual(tenant.appVersion, COUPON_MIN_APP_VERSION)) {
      navigate('../');
    }
  }, [navigate, tenant.appVersion]);

  if (isError) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3 p-6">
        <p className="text-sm text-muted-foreground">{LL.appHub.loadError()}</p>
        <Button variant="outline" onClick={() => void refetch()}>
          {LL.appHub.retry()}
        </Button>
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Loader2 aria-hidden className="animate-spin text-muted-foreground" />
      </div>
    );
  }
  return <CouponBannerEditor saved={data} />;
}

/** `saved` is the API's answer: `null` when the store has no banner yet (404). */
function CouponBannerEditor({ saved }: { saved: CouponBannerDto | null }) {
  const { LL } = useI18nContext();
  const tenant = useTenant();
  const chrome = useEditorChrome<TabId>('text');
  const { mutateAsync, isPending } = useSetCouponBanner();

  const [baseline, setBaseline] = useState(() => couponDtoToForm(saved));
  const [form, setForm] = useState(baseline);
  const [showErrors, setShowErrors] = useState(false);
  const [justPublished, setJustPublished] = useState(false);

  const errors = validateCouponForm(form);
  const dirty = isCouponFormDirty(baseline, form);
  useBlockerFull(dirty && !isPending);

  const update = (next: CouponBannerForm) => {
    setForm(next);
    setJustPublished(false);
  };

  const publishState: PublishState = isPending
    ? 'pending'
    : dirty
      ? 'dirty'
      : justPublished
        ? 'published'
        : 'clean';

  const publish = async () => {
    if (Object.keys(errors).length > 0) {
      setShowErrors(true);
      chrome.focusTarget('text');
      return;
    }
    try {
      await mutateAsync(couponFormToDto(form));
      setBaseline(form);
      setJustPublished(true);
      toast.success(LL.appEditor.published());
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : LL.appEditor.publishFailed()
      );
    }
  };

  return (
    <VisualEditorLayout<TabId>
      title={LL.appCustomizationPage.couponBanner.title()}
      backTo="../"
      chrome={chrome}
      active={{
        checked: form.isActive,
        onCheckedChange: (isActive) => update({ ...form, isActive }),
        label: LL.appEditor.active(),
      }}
      publish={{ state: publishState, onPublish: () => void publish() }}
      tabs={[
        {
          id: 'text',
          label: LL.couponEditor.tabs.text(),
          hasError: showErrors && Object.keys(errors).length > 0,
          content: (
            <CouponTextPanel
              form={form}
              onChange={update}
              errors={errors}
              showErrors={showErrors}
            />
          ),
        },
        {
          id: 'color',
          label: LL.couponEditor.tabs.color(),
          content: <CouponColorPanel form={form} onChange={update} />,
        },
        {
          id: 'position',
          label: LL.couponEditor.tabs.position(),
          content: <CouponPositionPanel form={form} onChange={update} />,
        },
        {
          id: 'effect',
          label: LL.couponEditor.tabs.effect(),
          content: <CouponEffectPanel form={form} onChange={update} />,
        },
      ]}
      canvas={
        <CouponStudioPreview
          form={form}
          storeName={tenant.name}
          iconUrl={tenant.iconUrl}
          onBannerPress={() => chrome.focusTarget('text')}
        />
      }
    />
  );
}
